import { h } from 'preact';
import Store from 'store/store';

import { joinClasses, sortObjectToList } from 'utils/utils';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';
import { hubPageUrls } from 'utils/data';
import { isFocusStudio, getCurrentHubDomain } from 'utils/studioConfig';
import * as jsonPointer from 'utils/jsonPointer';

import { showEditLinkModal } from 'components/main/navigation/editLink';
import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';
import HelpTooltip from 'components/tooltip/helpTooltip';
import MainContent from 'components/main/content';
import Button from 'components/button/button';
import SaveButton from 'components/button/saveButton';
import PreviewButton from 'components/button/previewButton';
import PublishButton from 'components/button/publishButton';
import VersionHistoryButton from 'components/button/versionHistoryButton';
import SimpleField from 'components/editor/settings/simpleField';

import s from 'components/main/navigation/navigation.sss';

export default class NavigationContent extends MainContent {
	constructor(props) {
		super(props);

		this.state = {};

		this.onReorder = this.onReorder.bind(this);
		this.addLink = this.addData.bind(this, { name: 'Link', destination: 'https://example.com' });
		this.showEditLinkModal = this.showEditLinkModal.bind(this);
		// Header Navigation
		this.addRootHeaderGroup = this.addData.bind(this, { name: 'Link Group', sublinks: [] }, '/header/links');
		this.addRootHeaderLink = this.addLink.bind(this, '/header/links');
		// Footer Navigation
		this.addFooterColumn = this.addData.bind(this, [], '/footer/links');
		// Legals Navigation
		this.addLegalLink = this.addLink.bind(this, '/footer/legal');
		// Movie Page Footer
		this.addMoviePageFooterLink = this.addDataItem.bind(this, '/moviePageFooter/links', { name: 'New link', destination: '' });
		this.addMoviePageCopyrightLine = this.addDataItem.bind(this, '/moviePageFooter/copyright', { name: 'New line', destination: '' });
		// Global Footer Copyright
		this.addFooterCopyrightLine = this.addData.bind(this, { name: 'New line', destination: '' }, '/footer/copyright');
		this.onFooterCopyrightReorder = this.onCopyrightReorder.bind(this, '/footer/copyright', 0);

		this.ensureNavigationDefaults();
		this.migrateMoviePageFooterToObjects();
	}

	componentDidMount() {
		[...this.base.querySelectorAll('[data-style]')].forEach(this.forceStyle);
	}

	componentDidUpdate() {
		[...this.base.querySelectorAll('[data-style]')].forEach(this.forceStyle);

		const pendingOpen = this.pendingOpen;
		if (pendingOpen) {
			this.pendingOpen = null;
			this.showEditLinkModal(pendingOpen);
		}
	}

	ensureNavigationDefaults() {
		this.ensureHubsiteHeaderDefaults();
		this.ensureHubsiteFooterDefaults();
		this.ensureMoviePageFooterDefaults();
	}

	ensureHubsiteHeaderDefaults() {
		const existingLinks = jsonPointer.get(Store.get().data, '/navigation/content/header/links');
		if (!existingLinks) {
			jsonPointer.setDeep(Store.get().data, '/navigation/content/header/links', [
				{ name: 'About', destination: 'about' },
				{ name: 'Explore', sublinks: [
					{ name: 'NBC Universal', destination: 'https://www.nbcuniversal.com/' },
					{ name: 'Universal Products & Experiences', destination: 'https://www.universalproductsexperiences.com/' },
					{ name: 'Universal Destinations & Experiences', destination: 'https://www.udx.com/' },
					{ name: 'Seen On The Screen Podcast', destination: 'https://www.youtube.com/playlist?list=PLuq_rgCzEP_Ne0W0nk6R66sYFGr1Jrzqe' },
				] },
			]);
		}
	}

	ensureHubsiteFooterDefaults() {
		const existingLinks = jsonPointer.get(Store.get().data, '/navigation/content/footer/links');
		if (!existingLinks) {
			jsonPointer.setDeep(Store.get().data, '/navigation/content/footer/links', [
				[
					{ name: 'Home', destination: 'landing' },
					{ name: 'About', destination: 'about' }
				],
				[
					{ name: 'NBC Universal', destination: 'https://www.nbcuniversal.com/' },
					{ name: 'Universal Products & Experiences', destination: 'https://www.universalproductsexperiences.com/' },
					{ name: 'Universal Studios Parks & Resorts', destination: 'http://themeparks.universalstudios.com/' },
					{ name: 'Seen On The Screen Podcast', destination: 'https://www.youtube.com/playlist?list=PLuq_rgCzEP_Ne0W0nk6R66sYFGr1Jrzqe' },
				]
			]);
		}

		const existingLegal = jsonPointer.get(Store.get().data, '/navigation/content/footer/legal');
		if (!existingLegal) {
			jsonPointer.setDeep(Store.get().data, '/navigation/content/footer/legal', [
				{ name: 'Terms of Service', destination: 'https://www.nbcuniversal.com/terms' },
				{ name: 'Privacy Policy', destination: 'https://www.nbcuniversalprivacy.com/privacy' },
				{ name: 'Cookie Preferences', destination: 'cookies' },
				{ name: 'Universal Studios', destination: 'https://www.udx.com/en/us' },
				{ name: 'Supply Chain Values', destination: 'https://corporate.comcast.com/company/impact/values-integrity' },
				{ name: 'Ad Choices', destination: 'https://www.nbcuniversalprivacy.com/privacy/cookies#accordionheader2' },
				{ name: 'Press', destination: 'https://www.upimedia.com/' },
				{ name: 'YouTube Terms of Service', destination: 'https://www.youtube.com/static?gl=GB&template=terms' },
				{ name: 'Google Privacy Policy', destination: 'https://policies.google.com/privacy?hl=en-GB' }
			]);
		}

		const existingCopyright = jsonPointer.get(Store.get().data, '/navigation/content/footer/copyright');
		if (!existingCopyright) {
			jsonPointer.setDeep(Store.get().data, '/navigation/content/footer/copyright', [
				{ name: '\u00A9 [Year] Universal Pictures' }
			]);
		}
	}

	ensureMoviePageFooterDefaults() {
		const existing = jsonPointer.get(Store.get().data, '/navigation/content/moviePageFooter');
		if (existing) return;

		jsonPointer.setDeep(Store.get().data, '/navigation/content/moviePageFooter', {
			links: {
				adChoices: { name: 'Ad Choices', destination: 'https://www.nbcuniversal.com/privacy/cookies#accordionHeader2', order: 0 },
				cookie: { name: 'Your Privacy Choices / Cookie Notice', destination: 'cookies', order: 1 },
				privacyPolicy: { name: 'Privacy Policy', destination: 'https://www.nbcuniversal.com/privacy', order: 2 },
				termsofUse: { name: 'Terms of Use', destination: 'https://www.nbcuniversal.com/terms', order: 3 },
			},
			copyright: {
				powster: { name: 'MOVIE PLATFORM \u00A9 [Year] POWSTER.', destination: 'https://powster.com', order: 0 },
				universal: { name: '\u00A9 [Year] Universal Pictures. ALL RIGHTS RESERVED.', destination: 'https://www.universalpictures.com', order: 1 },
			},
		});
	}

	migrateMoviePageFooterToObjects() {
		const { copyright, links } = Store.get().data?.navigation?.content?.moviePageFooter || {};

		if (copyright && Array.isArray(copyright)) {
			const newCopyright = Object.fromEntries(copyright.map((line, i) => [crypto.randomUUID(), { ...line, order: i }]));
			jsonPointer.setDeep(Store.get().data, '/navigation/content/moviePageFooter/copyright', newCopyright);
		}

		if (links && Array.isArray(links)) {
			const newLinks = Object.fromEntries(links.map((link, i) => [crypto.randomUUID(), { ...link, order: i }]));
			jsonPointer.setDeep(Store.get().data, '/navigation/content/moviePageFooter/links', newLinks);
		}
	}

	forceStyle(el) {
		if (!el || !el.dataset.style) return;
		// Issue: preact sets style via style.cssText, which strips unknown values
		//   but the anchor positioning polyfill needs it to be set, even if they are unknown
		el.setAttribute('style', el.dataset.style);
	}

	getTitle() {
		return 'Navigation & legal';
	}

	getDescription() {
		return 'Edit the site navigation';
	}

	onReorder(e) {
		const path = e.dragged.dataset.path;
		let parent = jsonPointer.get(Store.get().data.navigation?.content, jsonPointer.parse(path).slice(0, -1));
		const data = parent.at(e.from);
		parent = parent.splice(e.from, 1);
		parent = parent.splice(e.to, 0, data);
	}

	onCopyrightReorder(path, offset, e) {
		let copyright = jsonPointer.get(Store.get().data?.navigation?.content, path);
		if (!Array.isArray(copyright)) return;
		const from = e.from + offset;
		const to = e.to + offset;
		const data = copyright.at(from);
		copyright = copyright.splice(from, 1);
		copyright = copyright.splice(to, 0, data);
	}

	onMoviePageReorder(e) {
		const basePath = `/navigation/content`;
		const allItems = [...e.target.querySelectorAll('[data-path]')].map(el => el.dataset.path);
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);
		allItems.forEach((path, i) => {
			jsonPointer.setDeep(Store.get().data, `${basePath}${path}/order`, i + 1);
		});
	}

	addData(data, path, e) {
		const newArray = jsonPointer.get(Store.get().data, jsonPointer.concat('/navigation/content', path)).push(data);
		const popover = e?.target?.closest('[popover]');
		if (popover) popover.hidePopover();
		// Do not automatically open non-named columns
		if (!Array.isArray(data)) {
			this.pendingOpen = `${path}/${newArray.length - 1}`;
		}
	}

	addDataItem(path, data, e) {
		const dataRoot = `/navigation/content${path}`;
		const id = crypto.randomUUID();

		// Calculate the order for the new data item based on existing entries
		const existingEntries = jsonPointer.get(Store.get().data, dataRoot) || {};
		const order = Object.keys(existingEntries).length + 1;

		// Add the new data item with the calculated order
		jsonPointer.setDeep(Store.get().data, `${dataRoot}/${id}`, { ...data, order });

		// Close existing popover and open the new one
		const popover = e?.target?.closest('[popover]');
		if (popover) popover.hidePopover();
		this.pendingOpen = `${path}/${id}`;
	}

	getLinkTargetPages(path) {
		if (isFocusStudio()) {
			return [
				{ id: 'landing', name: 'Landing' },
				{ id: 'about', name: 'About' },
				{ id: 'contact', name: 'Contact Us' },
				{ id: 'terms-of-use', name: 'Terms of Use' },
			];
		}
		// NOTE: different regions might have different slugs - how should we match them ?
		const availablePages = Object.values(Store.get().data?.pages || {}).map(p => p.slug).concat(
			// releases is not a page editable in the CMS, but is a valid target for links in all regions
			'releases',
		);
		const pages = [
			{ id: 'landing', name: 'Landing' },
			{ id: 'about', name: 'About' },
			{ id: 'news', name: 'News' },
			{ id: 'releases', name: 'Future Releases' },
			{ id: 'leadership', name: 'Leadership Team' },
		].filter(p => availablePages.includes(p.id));
		if (path?.startsWith('/moviePageFooter/')) {
			const hubDomain = getCurrentHubDomain();
			return pages.map(e => ({ ...e, id: `https://${hubDomain}${hubPageUrls.get(e.id) || `/${e.id}/`}` }));
		}
		return pages;
	}

	// Page targets are stored as a page id everywhere except the movie page footer, which stores a full hub URL
	getLinkTypeClass(link, pages = []) {
		const destination = link?.destination;
		if (!destination) return null;
		if (destination === 'cookies') return s.cookies;
		if (pages.some(page => page.id === destination)) return s.page;
		return destination.startsWith('http') ? s.external : null;
	}

	async showEditLinkModal(e) {
		const linkPath = e instanceof Event ? e.target?.closest('[data-path]')?.dataset.path : e;
		if (!linkPath) return;
		const data = jsonPointer.get(Store.get().data.navigation?.content, linkPath);

		const options = {
			link: data,
			anchor: `--header-link-${linkPath.replaceAll('/', '_')}`,
			deletable: !linkPath.startsWith('/footer/social'),
		};
		if (linkPath.startsWith('/footer/copyright') || linkPath.startsWith('/moviePageFooter/copyright')) {
			options.targetTypes = ['none', 'external'];
			options.namePreview = { replacements: { '[year]': new Date().getFullYear() } };
		} else {
			options.targetTypes = ['external', 'page', 'cookies'];
			options.pages = this.getLinkTargetPages(linkPath);
		}

		const results = await showEditLinkModal(options);

		if (results?.type == 'DELETE') {
			const type = data.sublinks ? 'group' : 'link';
			const ok = await confirm(`This will delete the ${type === 'link' ? 'link' : 'group and its contents'} from the navigation menu and cannot be undone`, { title: `Delete ${type}?`, danger: true, button: `Delete ${type}` });
			if (!ok) return;

			const parent = jsonPointer.get(Store.get().data.navigation?.content, jsonPointer.parse(linkPath).slice(0, -1));
			if (Array.isArray(parent)) {
				parent.splice(parent.indexOf(data), 1);
			} else if (linkPath.startsWith('/moviePageFooter/')) {
				// Soft delete so movies overriding this item drop it too
				jsonPointer.setDeep(Store.get().data.navigation?.content, linkPath, null);
			} else {
				jsonPointer.removeDeep(Store.get().data.navigation?.content, linkPath);
			}
		} else if (results) {
			// Preserve data not set by the EditLinkModal (e.g. sublinks)
			const dataToSave = Object.assign({}, data, results);
			jsonPointer.set(Store.get().data.navigation?.content, linkPath, dataToSave);
		}
	}

	renderLinks(links, path = '') {
		if (!links || !Array.isArray(links)) return;
		const readonly = !canUser('write');
		const pages = this.getLinkTargetPages(path);
		return links.map((link, i) => {
			if (!link) return;
			const linkPath = `${path}/${link.id || i}`;
			let sublinksPath = `${linkPath}/sublinks`;
			if (Array.isArray(link)) {
				link = { name: `Column ${i + 1}`, sublinks: link };
				sublinksPath = linkPath;
			}
			return (
				<SortableItem class={joinClasses(s.link, this.getLinkTypeClass(link, pages))} key={link.id || i} data-path={linkPath}>
					<div class={s.nameWrapper}>
						<Handle class={s.dragHandle} />
						<button class={s.name} onClick={this.showEditLinkModal} data-style={`anchor-name: --header-link-${linkPath.replaceAll('/', '_')};`}>
							{link.name}
							{link.destination === 'cookies' && (
								<HelpTooltip class={s.tooltip} direction="top" align="start">Text will change automatically depending on the user region.</HelpTooltip>
							)}
						</button>
						{!!link.sublinks && <Button class={s.addSub} icon="plus" onClick={() => this.addLink(sublinksPath)} disabled={readonly}>Add sub-link</Button>}
					</div>
					{!!link.sublinks && (
						<Sortable class={s.sublinks} onChange={this.onReorder} disabled={readonly}>
							{this.renderLinks(link.sublinks, sublinksPath)}
						</Sortable>
					)}
				</SortableItem>
			);
		});
	}

	renderSocialLinks(links, path = '') {
		if (!links || !Array.isArray(links)) return;
		const pages = this.getLinkTargetPages(path);
		return links.map((link, i) => {
			if (!link) return;
			const linkPath = `${path}/${i}`;
			return (
				<SortableItem key={i} class={joinClasses(s.link, this.getLinkTypeClass(link, pages))} data-path={linkPath}>
					<div class={s.nameWrapper}>
						<Handle class={s.dragHandle} />
						<button class={joinClasses(s.name, s.socials)} onClick={this.showEditLinkModal} data-style={`anchor-name: --header-link-${linkPath.replaceAll('/', '_')};`}>{link.name}</button>
					</div>
				</SortableItem>
			);
		});
	}

	renderContent() {
		const content = this.props.data?.navigation?.content;
		const headerLinks = content?.header?.links || [];
		const footerLinks = content?.footer?.links || [];
		const legalLinks = content?.footer?.legal || [];
		const socialLinks = content?.footer?.social || [];
		const readonly = !canUser('write');
		const isFocus = Store.get().domains?.isFocus;

		return (
			<div class={s.content}>
				{!isFocus && (
					<div>
						<h2 class={s.header}>Navigation</h2>
						<h3 class={s.sectionTitle}>Navigation</h3>
						<Sortable class={s.links} onChange={this.onReorder} disabled={readonly}>
							{this.renderLinks(headerLinks, '/header/links')}
							<Button icon="plus" class={s.add} popovertarget="nav-header-add-top-link" disabled={readonly}>
								Add
								<div popover="auto" id="nav-header-add-top-link">
									<Button onClick={this.addRootHeaderGroup}>Group</Button>
									<Button onClick={this.addRootHeaderLink}>Link</Button>
								</div>
							</Button>
						</Sortable>
					</div>
				)}

				<h2 class={s.header}>Footer</h2>
				<h3 class={s.sectionTitle}>Footer Links</h3>
				<Sortable class={s.links} onChange={this.onReorder} disabled={readonly}>
					{this.renderLinks(footerLinks, '/footer/links')}
					{(!isFocus || footerLinks.length < 2) && <Button icon="plus" class={s.add} onClick={this.addFooterColumn} disabled={readonly}>Add column</Button>}
				</Sortable>

				{isFocus ? (
					<div>
						<h3 class={s.sectionTitle}>Social links</h3>
						<Sortable class={joinClasses(s.links, s.legal)} onChange={this.onReorder} disabled={readonly}>
							{this.renderSocialLinks(socialLinks, '/footer/social')}
						</Sortable>
					</div>
				) : (
					<div>
						<h3 class={s.sectionTitle}>Legal links</h3>
						<Sortable class={joinClasses(s.links, s.legal)} onChange={this.onReorder} disabled={readonly}>
							{this.renderLinks(legalLinks, '/footer/legal')}
							<Button icon="plus" class={s.add} onClick={this.addLegalLink} disabled={readonly}>
								Add link
							</Button>
						</Sortable>
					</div>
				)}

				{isFocus && typeof content?.footer?.legal === 'string' && (
					<SimpleField
						class={s.legalField}
						label="Footer legals"
						path="/navigation/content/footer/legal"
						multiline
						disabled={readonly}
					/>
				)}

				{!isFocus && this.renderFooterCopyrightSection(content, readonly)}

				{!isFocus && this.renderMoviePageFooterSection(content, readonly)}
			</div>
		);
	}

	renderFooterCopyrightSection(content, readonly) {
		const currentYear = new Date().getFullYear();
		const copyrightLines = content?.footer?.copyright || [];

		return (
			<div>
				<h3 class={s.sectionTitle}>Footer copyright</h3>
				<Sortable class={joinClasses(s.links, s.legal)} onChange={this.onFooterCopyrightReorder} disabled={readonly}>
					{copyrightLines.map((line, i) => {
						const linkPath = `/footer/copyright/${i}`;
						return (
							<SortableItem class={joinClasses(s.link, line.destination && s.external)} data-path={linkPath}>
								<div class={s.nameWrapper}>
									<Handle class={s.dragHandle} />
									<button class={s.name} onClick={this.showEditLinkModal} data-style={`anchor-name: --header-link-${linkPath.replaceAll('/', '_')};`}>{line.name}</button>
								</div>
							</SortableItem>
						);
					})}
					<Button icon="plus" class={s.add} onClick={this.addFooterCopyrightLine} disabled={readonly}>
						Add
					</Button>
				</Sortable>

				{copyrightLines.length > 0 && (
					<div class={s.copyrightPreview}>
						<h4 class={s.previewTitle}>Copyright preview</h4>
						<div class={s.previewText}>
							{copyrightLines.map((line, i) => (
								<p key={i} style={{ margin: 0 }}>{line.name.replace(/\[Year\]/gi, currentYear)}</p>
							))}
						</div>
					</div>
				)}
			</div>
		);
	}

	renderMoviePageFooterSection(content, readonly) {
		const currentYear = new Date().getFullYear();
		const { copyright, links } = this.props.data?.navigation?.content?.moviePageFooter || {};
		const sortedLinks = sortObjectToList(links);
		const sortedCopyright = sortObjectToList(copyright);

		return (
			<div class={s.section}>
				<h2 class={s.header}>Movie page footer</h2>
				<p class={s.sectionDescription}>Set the default footer links for all movie pages</p>

				<h3 class={s.sectionTitle}>Footer links</h3>
				<Sortable class={joinClasses(s.links, s.legal)} onChange={this.onMoviePageReorder} disabled={readonly}>
					{this.renderLinks(sortedLinks, '/moviePageFooter/links')}
					<Button icon="plus" class={s.add} onClick={this.addMoviePageFooterLink} disabled={readonly}>
						Add
					</Button>
				</Sortable>

				<h3 class={s.sectionTitle}>Footer copyright</h3>
				<Sortable class={joinClasses(s.links, s.legal)} onChange={this.onMoviePageReorder} disabled={readonly}>
					{sortedCopyright.map((line, i) => {
						const linkPath = `/moviePageFooter/copyright/${line.id}`;
						if (i === 0) {
							return (
								<div class={joinClasses(s.link, s.external, s.lockedLine)}>
									<div class={s.nameWrapper}>
										<span class={s.name}>{line.name}</span>
									</div>
								</div>
							);
						}
						return (
							<SortableItem class={joinClasses(s.link, line.destination && s.external)} data-path={linkPath} key={line.id}>
								<div class={s.nameWrapper}>
									<Handle class={s.dragHandle} />
									<button class={s.name} onClick={this.showEditLinkModal} data-style={`anchor-name: --header-link-${linkPath.replaceAll('/', '_')};`}>{line.name}</button>
								</div>
							</SortableItem>
						);
					})}
					<Button icon="plus" class={s.add} onClick={this.addMoviePageCopyrightLine} disabled={readonly}>
						Add
					</Button>
				</Sortable>

				{sortedCopyright.length > 0 && (
					<div class={s.copyrightPreview}>
						<h4 class={s.previewTitle}>Copyright preview</h4>
						<div class={s.previewText}>
							{sortedCopyright.map(line => (
								<p key={line.id} style={{ margin: 0 }}>{line.name.replace(/\[Year\]/gi, currentYear)}</p>
							))}
						</div>
					</div>
				)}
			</div>
		);
	}

	renderButtons() {
		const hideSave = !canUser('write');
		const hidePublish = !canUser('publish');

		return [
			<VersionHistoryButton path="/navigation" key="history" />,
			!hideSave && <SaveButton path="/navigation" key="save" />,
			<PreviewButton name="navigation" path="/navigation" key="preview" />,
			!hidePublish && <PublishButton dark path="/navigation" key="publish" />,
		];
	}
}
